import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import type { QuickPanelKey, QuickPanelParams } from '@/app/surfaces/quick-panel-registry.js';

export interface QuickPanelStackEntry {
  id: string;
  key: QuickPanelKey;
  params: QuickPanelParams;
  pinned: boolean;
}

export interface QuickPanelContextValue {
  stack: QuickPanelStackEntry[];
  openQuickPanel: (key: QuickPanelKey, params?: QuickPanelParams) => void;
  replaceQuickPanel: (key: QuickPanelKey, params?: QuickPanelParams) => void;
  closeQuickPanel: () => void;
  closeQuickPanelById: (entryId: string) => void;
  /** Removes the entry and every level stacked above it (BR-IDT-45, BR-IDT-46). */
  closeQuickPanelsFrom: (entryId: string) => void;
  /** Removes only the levels stacked above the entry, keeping it open (BR-IDT-47). */
  closeQuickPanelsAbove: (entryId: string) => void;
  /** Shell only — route change or work-context switch (BR-IDT-45 exception). */
  closeAllQuickPanels: () => void;
  /**
   * Declares how a stacked panel dismisses itself, so header × and Esc close the same
   * levels its own Save/Cancel would (BR-IDT-48).
   */
  registerQuickPanelDismiss: (entryId: string, dismiss: (() => void) | null) => void;
  dismissQuickPanelById: (entryId: string) => void;
  dismissTopQuickPanel: () => void;
  toggleQuickPanel: (key: QuickPanelKey, params?: QuickPanelParams) => void;
  togglePin: (entryId: string) => void;
  isOpen: (key: QuickPanelKey) => boolean;
}

export const QuickPanelContext = createContext<QuickPanelContextValue | null>(null);

export function useQuickPanelContext(): QuickPanelContextValue {
  const value = useContext(QuickPanelContext);

  if (!value) {
    throw new Error('useQuickPanelContext must be used within QuickPanelProvider');
  }

  return value;
}

interface QuickPanelProviderProps {
  children: ReactNode;
}

function createPanelId(): string {
  return crypto.randomUUID();
}

function quickPanelParamsEqual(a: QuickPanelParams, b: QuickPanelParams): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);

  for (const key of keys) {
    const valueA = a[key];
    const valueB = b[key];

    if (typeof valueA === 'function' || typeof valueB === 'function') {
      continue;
    }

    if (Array.isArray(valueA) && Array.isArray(valueB)) {
      if (valueA.length !== valueB.length) {
        return false;
      }
      if (valueA.some((item, index) => !Object.is(item, valueB[index]))) {
        return false;
      }
      continue;
    }

    if (!Object.is(valueA, valueB)) {
      return false;
    }
  }

  return true;
}

export function QuickPanelProvider({ children }: QuickPanelProviderProps) {
  const [stack, setStack] = useState<QuickPanelStackEntry[]>([]);
  const dismissHandlers = useRef(new Map<string, () => void>());

  const openQuickPanel = useCallback((key: QuickPanelKey, params: QuickPanelParams = {}) => {
    setStack((current) => {
      const existingIndex = current.findIndex(
        (entry) => entry.key === key && quickPanelParamsEqual(entry.params, params),
      );

      if (existingIndex !== -1) {
        const existing = current[existingIndex];
        if (!existing) {
          return current;
        }

        return [...current.filter((_, index) => index !== existingIndex), existing];
      }

      const withoutSameKey = current.filter((entry) => entry.key !== key);

      return [...withoutSameKey, { id: createPanelId(), key, params, pinned: false }];
    });
  }, []);

  const replaceQuickPanel = useCallback((key: QuickPanelKey, params: QuickPanelParams = {}) => {
    setStack((current) => {
      const next = current.slice(0, -1);
      return [...next, { id: createPanelId(), key, params, pinned: false }];
    });
  }, []);

  const closeQuickPanel = useCallback(() => {
    setStack((current) => {
      const top = current.at(-1);
      if (!top || top.pinned) {
        return current;
      }

      return current.slice(0, -1);
    });
  }, []);

  const closeQuickPanelById = useCallback((entryId: string) => {
    setStack((current) => {
      const entry = current.find((item) => item.id === entryId);
      if (!entry || entry.pinned) {
        return current;
      }

      return current.filter((item) => item.id !== entryId);
    });
  }, []);

  const closeQuickPanelRange = useCallback((entryId: string, includeEntry: boolean) => {
    setStack((current) => {
      const index = current.findIndex((item) => item.id === entryId);
      if (index === -1) {
        return current;
      }

      const firstRemoved = includeEntry ? index : index + 1;
      const next = current.filter((item, itemIndex) => itemIndex < firstRemoved || item.pinned);

      return next.length === current.length ? current : next;
    });
  }, []);

  const closeQuickPanelsFrom = useCallback(
    (entryId: string) => {
      closeQuickPanelRange(entryId, true);
    },
    [closeQuickPanelRange],
  );

  const closeQuickPanelsAbove = useCallback(
    (entryId: string) => {
      closeQuickPanelRange(entryId, false);
    },
    [closeQuickPanelRange],
  );

  const closeAllQuickPanels = useCallback(() => {
    setStack([]);
  }, []);

  const registerQuickPanelDismiss = useCallback((entryId: string, dismiss: (() => void) | null) => {
    if (dismiss === null) {
      dismissHandlers.current.delete(entryId);
      return;
    }

    dismissHandlers.current.set(entryId, dismiss);
  }, []);

  const dismissQuickPanelById = useCallback(
    (entryId: string) => {
      const dismiss = dismissHandlers.current.get(entryId);
      if (dismiss) {
        dismiss();
        return;
      }

      closeQuickPanelById(entryId);
    },
    [closeQuickPanelById],
  );

  const dismissTopQuickPanel = useCallback(() => {
    const top = stack.at(-1);
    if (!top || top.pinned) {
      return;
    }

    dismissQuickPanelById(top.id);
  }, [dismissQuickPanelById, stack]);

  const toggleQuickPanel = useCallback((key: QuickPanelKey, params: QuickPanelParams = {}) => {
    setStack((current) => {
      const index = current.findIndex((entry) => entry.key === key);
      if (index === -1) {
        return [...current, { id: createPanelId(), key, params, pinned: false }];
      }

      return current.filter((_, entryIndex) => entryIndex !== index);
    });
  }, []);

  const togglePin = useCallback((entryId: string) => {
    setStack((current) =>
      current.map((entry) => (entry.id === entryId ? { ...entry, pinned: !entry.pinned } : entry)),
    );
  }, []);

  const isOpen = useCallback(
    (key: QuickPanelKey) => stack.some((entry) => entry.key === key),
    [stack],
  );

  const value = useMemo<QuickPanelContextValue>(
    () => ({
      stack,
      openQuickPanel,
      replaceQuickPanel,
      closeQuickPanel,
      closeQuickPanelById,
      closeQuickPanelsFrom,
      closeQuickPanelsAbove,
      closeAllQuickPanels,
      registerQuickPanelDismiss,
      dismissQuickPanelById,
      dismissTopQuickPanel,
      toggleQuickPanel,
      togglePin,
      isOpen,
    }),
    [
      closeAllQuickPanels,
      closeQuickPanel,
      closeQuickPanelById,
      closeQuickPanelsAbove,
      closeQuickPanelsFrom,
      dismissQuickPanelById,
      dismissTopQuickPanel,
      isOpen,
      openQuickPanel,
      registerQuickPanelDismiss,
      replaceQuickPanel,
      stack,
      togglePin,
      toggleQuickPanel,
    ],
  );

  return <QuickPanelContext.Provider value={value}>{children}</QuickPanelContext.Provider>;
}
